import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAcknowledgeAnnouncement, useMarkAnnouncementRead, useRelayAnnouncement, useDeleteAnnouncement } from '../../hooks/useAnnouncements';
import { Announcement } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useCurrentUser } from '../../contexts/AuthContext';
import { can } from '../../utils/permissions';
import { X, CheckCircle2, Layers, Link as LinkIcon, Eye, Share2, Trash2 } from 'lucide-react';
import { safeDistanceToNow, cn } from '../../lib/utils';
import { useTenant } from '../../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import { apiWorker } from '../../api/worker';
import { Clock } from 'lucide-react';

import { RichContentViewer } from '../RichContentViewer';

interface AnnouncementCardProps {
  announcement: Announcement;
  isAdminView?: boolean;
  onDismiss?: (id: string) => void;
}

const formatAuthorRole = (role?: string): string => {
  if (!role) return '';
  const normalized = role.trim().toUpperCase();
  const ROLE_MAP: Record<string, string> = {
    'SUPER_ADMIN': 'System Administrator',
    'SYSTEM_ADMIN': 'System Administrator',
    'OPERATIONS_ADMIN': 'Operations Administrator',
    'SUPPORT_ADMIN': 'Support Administrator',
    'ADMIN': 'Administrator',
    'OWNER': 'Owner',
    'LEADER': 'Leader',
    'MEMBER': 'Member',
  };
  if (ROLE_MAP[normalized]) return ROLE_MAP[normalized];
  return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export const AnnouncementCard: React.FC<AnnouncementCardProps> = ({ 
  announcement,
  isAdminView = false,
  onDismiss
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { user, memberships } = useCurrentUser();
  const { node, brand } = useTenant();
  const navigate = useNavigate();
  const acknowledgeMutation = useAcknowledgeAnnouncement(announcement.nodeId);
  const relayMutation = useRelayAnnouncement(announcement.nodeId);
  const readMutation = useMarkAnnouncementRead(announcement.nodeId);
  const deleteMutation = useDeleteAnnouncement(announcement.nodeId);

  const userRole = memberships.find(m => m.id === announcement.nodeId || m.nodeName === `${brand?.name} Global`)?.role || 'MEMBER';
  const isAuthor = user?.id === announcement.authorUserId;
  const showAdminStats = isAdminView || can(userRole, 'create_announcement');
  
  const terms = node?.config?.terminology;
  const isFromParent = node && announcement.nodeId !== node.id;

  const effectiveLink = announcement.linkUrl || (announcement as any).link;

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!effectiveLink) return;

    if (effectiveLink.startsWith('/') || effectiveLink.startsWith('#')) {
      navigate(effectiveLink);
    } else {
      window.open(effectiveLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAcknowledge = (e: React.MouseEvent) => {
    e.stopPropagation();
    acknowledgeMutation.mutate(announcement.id);
  };

  const handleRelay = (e: React.MouseEvent) => {
    e.stopPropagation();
    relayMutation.mutate(announcement.id);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissed(true);
    readMutation.mutate(announcement.id);
    if (onDismiss) {
      onDismiss(announcement.id);
    }
    toast.success('Announcement dismissed');
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteMutation.mutate(announcement.id);
  };

  const timeAgo = safeDistanceToNow(announcement.createdAt) + ' ago';
  const expiresAt = announcement.expiresAt ? safeDistanceToNow(announcement.expiresAt) : null;
  const isExpired = announcement.expiresAt ? new Date(announcement.expiresAt) < new Date() : false;

  React.useEffect(() => {
    if (announcement.id) {
      apiWorker.post(`/api/nodes/${announcement.nodeId}/pulse/${announcement.id}/view`).catch(() => {});
    }
  }, [announcement.id]);

  // Mock reach for progress bar (in a real app this would come from the backend)
  const estimatedReach = 100; 
  const ackCount = announcement.acknowledgedBy?.length || 0;
  const ackRate = Math.round((ackCount / estimatedReach) * 100);

  if (isDismissed) {
    return null;
  }

  return (
    <Card 
      variant="announcement" 
      severity={announcement.severity} 
      className={cn(
        "relative group overflow-hidden transition-all duration-300",
        announcement.severity === 'CRITICAL' && !announcement.isAcknowledged && "ring-2 ring-red-500 animate-pulse-subtle shadow-lg shadow-red-500/10"
      )}
    >
      {/* Acknowledgement Progress Bar (Admin only) */}
      {showAdminStats && announcement.requiresAck && (
        <div className="absolute top-0 left-0 w-full h-1 bg-black/5">
          <div 
            className="h-full bg-current transition-all duration-1000 ease-out" 
            style={{ width: `${Math.min(ackRate, 100)}%`, opacity: 0.3 }} 
          />
        </div>
      )}

      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge 
              variant={
                announcement.severity === 'CRITICAL' ? 'red' :
                announcement.severity === 'HIGH' ? 'amber' :
                announcement.severity === 'MEDIUM' ? 'blue' : 'gray'
              }
            >
              {announcement.type} • {announcement.severity}
            </Badge>
            {announcement.cascadesDown && (
              <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">
                <Layers size={10} />
                <span>Cascading</span>
              </div>
            )}
            {isFromParent && announcement.nodeName && (
              <Badge variant="blue" className="bg-brand-primary border-none text-[10px] uppercase font-mono tracking-wider">
                {terms?.parentNode || 'From'}: {announcement.nodeName}
              </Badge>
            )}
          </div>
          
          <h3 className="text-base font-bold mb-1 text-gray-900 dark:text-white">{announcement.title}</h3>
          
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
            <span>By {announcement.authorName || 'System Admin'}</span>
            {announcement.authorRole && (
              <>
                <span>•</span>
                <span className="font-semibold text-gray-700 dark:text-gray-200">{formatAuthorRole(announcement.authorRole)}</span>
              </>
            )}
            <span>•</span>
            <span>{timeAgo}</span>
            {expiresAt && !isExpired && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-orange-600 font-medium">
                  <Clock size={10} />
                  Expires in {expiresAt}
                </span>
              </>
            )}
            {isExpired && (
              <>
                <span>•</span>
                <span className="text-red-500 font-bold uppercase tracking-tighter">EXPIRED</span>
              </>
            )}
          </div>

          <div className="text-sm leading-relaxed mb-4">
            <RichContentViewer content={announcement.body} />
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            {effectiveLink && (
              <Button 
                size="sm" 
                variant="secondary" 
                className="bg-white text-black dark:bg-black dark:text-white border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black gap-1.5 font-mono text-xs"
                onClick={handleLinkClick}
              >
                <LinkIcon size={14} />
                {announcement.linkText || effectiveLink}
              </Button>
            )}

            {announcement.requiresAck && !announcement.isAcknowledged && (
              <Button 
                size="sm" 
                variant="primary" 
                className={cn(
                  announcement.severity === 'CRITICAL' ? "bg-red-700 hover:bg-red-800" : "bg-navy-900 hover:bg-navy-800",
                  "text-white border-none"
                )}
                onClick={handleAcknowledge}
                isLoading={acknowledgeMutation.isPending}
              >
                <CheckCircle2 size={14} />
                {announcement.severity === 'CRITICAL' ? 'Requires acknowledgement' : 'Acknowledge'}
              </Button>
            )}

            {isFromParent && announcement.cascadesDown && can(userRole, 'create_announcement') && (
              <Button
                size="sm"
                variant="ghost"
                className="hover:bg-brand-primary/5 border border-gray-200"
                onClick={handleRelay}
                isLoading={relayMutation.isPending}
              >
                <Share2 size={14} />
                Relay Downstream
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {announcement.severity !== 'CRITICAL' && !announcement.resolved && (
            <button 
              onClick={handleDismiss}
              className="p-1 rounded-none border border-black/10 hover:border-black dark:border-white/10 dark:hover:border-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer text-gray-400 hover:text-black dark:hover:text-white"
              title="Dismiss"
              aria-label="Dismiss Announcement"
            >
              <X size={16} />
            </button>
          )}

          {(isAuthor || showAdminStats) && (
            <button 
              onClick={handleDelete}
              className="p-1 rounded-md text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
              title="Delete Announcement"
            >
              <Trash2 size={16} />
            </button>
          )}

          {showAdminStats && (
            <div className="flex flex-col items-end gap-1 mt-auto">
              {announcement.requiresAck && (
                <div className="text-[10px] font-bold uppercase tracking-widest font-mono opacity-50 text-right">
                  {ackCount} / {estimatedReach} ack ({ackRate}%)
                </div>
              )}
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest font-mono opacity-50">
                <Eye size={10} />
                <span>{announcement.viewCount || 0} views • {announcement.relayCount} relays</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
